import React, { useState } from "react";
import Footer from "./Footer";
import { ChevronRight } from "lucide-react";
import SampleImg from "../../../assets/images/HologramExample.jpg";
import useStickyButton from "../hooks/useStickyButton";
import isMobileDevice from "../../../utils/isMobileDevice";
import DesktopQrModal from "../../../components/DesktopQrModal";
import SecureCamera from "../../../components/SecureCamera/SecureCamera";

const GuidelineHologramDetection = (props) => {
  const { onNext, uiConfig } = props;

  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();
  const [showDesktopNotice, setShowDesktopNotice] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const handleOpenCamera = () => {
    if (!isMobileDevice()) {
      setShowDesktopNotice(true);
      return;
    }

    setShowDesktopNotice(false);
    setShowCamera(true);
  };

  const global = uiConfig?.Global || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white

  const handleSecureCapture = ({ file }) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      onNext(reader.result);
    };
  };

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
        color: textColor,
      }}
    >
      {/* Title */}
      <h2
        style={{
          color: textColor,
          fontSize: "1.25rem",
          fontWeight: "bold",
          marginBottom: "1rem",
          textAlign: "center",
        }}
      >
        Capture the
        <span style={{ color: highlightTextColor }}> Hologram</span> <br />
        on Your <span style={{ color: highlightTextColor }}>MyKad</span>
      </h2>
      <p
        style={{
          marginBottom: "1rem",
          textAlign: "left",
          maxWidth: "prose",
          color: textColor,
        }}
      >
        Please take a photo of your MyKad showing the hologram area clearly. This helps us verify your MyKad's
        authenticity.
      </p>

      <p
        style={{
          marginBottom: "1rem",
          textAlign: "left",
          maxWidth: "prose",
        }}
      >
        Please ensure:
      </p>

      <ul className="space-y-2">
        <li className="flex items-center space-x-2">
          <ChevronRight className="text-blue-500" />
          <span style={{ color: textColor }}>Position your entire MyKad within the camera</span>
        </li>
        <li className="flex items-center space-x-2">
          <ChevronRight className="text-blue-500" />
          <span style={{ color: textColor }}>Ensure the hologram is clearly visible</span>
        </li>
      </ul>

      <div className="flex items-center justify-center mb-16 mt-8">
        <img
          src={SampleImg}
          alt="Sample of how to take a good picture of your IC"
          width={"auto"}
          height={"140px"}
          style={{ maxHeight: "180px" }}
          className="shadow-md shadow-black"
        />
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      <div 
        ref={buttonContainerRef}
        style={{
          position: "sticky",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          padding: "1rem",
          background: "transparent",
          zIndex: "20",
          transition: "all 0.3s ease-in-out",
          marginTop: "auto",
        }}
      >
        <button
          style={{
            background: buttonBgColor,
            color: buttonTextColor,
            width: "66.666667%",
            padding: "0.75rem 1rem",
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            margin: "0 auto",
            transition: "transform 0.15s ease-in-out",
            pointerEvents: "auto",
          }}
          onClick={handleOpenCamera}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
          onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span>Continue</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <DesktopQrModal
        open={showDesktopNotice}
        onClose={() => setShowDesktopNotice(false)}
      />

      <SecureCamera
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleSecureCapture}
        frameType="ID"
        title="Place your MyKad within the frame, hologram visible"
        theme={{
          primaryColor: buttonBgColor,
          textColor: "#ffffff",
        }}
      />

      <Footer
        style={{
          fontSize: "0.75rem",
          color: textColor,
          marginTop: "1rem",
        }}
      />
    </div>
  );
};

export default GuidelineHologramDetection;
